/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import Grid from '@material-ui/core/Grid';
import { useMediaQuery } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import ReactMarkdown from 'react-markdown';
import { makeStyles } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import CachedIcon from '@material-ui/icons/Cached';
import {
    shape, string, arrayOf, number, bool, func,
} from 'prop-types';
import SubscriptionPDP from '../../../SubscriptionInformation/SubscriptionPDP';
import SubscriptionDropDown from '../SubscriptionDropDown';

const useStyles = makeStyles((theme) => ({
    mainSubscribeBox: {
        padding: '10px 15px',
        border: `1px solid ${(theme.palette.grey8)}`,
    },
    marginBottom15: {
        marginBottom: '15px',
    },
    subContainer: {
        display: 'flex',
        '& > div': {
            margin: '0px 10px',
            [theme.breakpoints.down('md')]: {
                margin: '0px',
            },

        },
        [theme.breakpoints.down('xs')]: {
            '& > div': {
                width: '100%',
            },
        },
    },
    [theme.breakpoints.down('xs')]: {
        subContainerSm: {
            marginTop: 15,
        },
    },
    icon: {
        color: theme?.palette?.cms?.icon,
        margin: 'auto',
    },
    borderRight1: {
        [theme.breakpoints.up('sm')]: {
            borderRight: `1px solid ${(theme.palette.grey8)}`,
        },
    },
    marginTopBottom10: {
        margin: '10px 0px',
    },
    subcriptionContent: {
        '& p': {
            fontSize: 16,
            margin: 4,
            [theme.breakpoints.down('xs')]: {
                margin: 2,
            },
            [theme.breakpoints.down('sm')]: {
                fontSize: 15,
            },
        },
    },
    fontSize67: {
        fontSize: 64,
        [theme.breakpoints.down('md')]: {
            fontSize: 45,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: 35,
        },
        [theme.breakpoints.down('xs')]: {
            paddingLeft: 4,
            fontSize: 38,
        },

    },
    fontSize34: {
        fontSize: '34px',
    },
    subscriptionCheckBoxContent: {
        fontSize: 14,
        '& b': {
            fontSize: 16,
            display: 'block',
        },
    },
    checkbox: {
        color: theme.palette.cms?.checkbox,
        height: '100%',
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
            marginTop: '5px',
        },
        '&$checked': {
            color: theme.palette.cms?.checkbox,
            '&:hover': {
                backgroundColor: theme.palette.colorNeutral20,
            },
        },
        '&:hover': {
            backgroundColor: theme.palette.colorNeutral20,
        },
        '& span svg': {
            fontSize: '24px',
        },
    },
    checked: {
        color: theme.palette.cms?.checkbox,
        '&$checked': {
            color: theme.palette.cms?.checkbox,
            '&:hover': {
                backgroundColor: theme.palette.colorNeutral20,
            },
        },
    },
    marginNone: {
        '& p': {
            margin: 0,
        },
    },
    inline: {
        display: 'inline-block',
    },
    maxWidth: {
        maxWidth: '100%',
    },
    alignImage: {
        alignItems: 'center',
    },
    paddingLeft: {
        paddingLeft: '5px',
    },
    alignSpan: {
        alignItems: 'center',
        [theme.breakpoints.up('sm')]: {
            paddingRight: '5px',
            borderRight: `1px solid ${(theme.palette.grey8)}`,
        },
        [theme.breakpoints.down('sm')]: {
            marginBottom: '15px',
        },
    },
    subscribeHeader: {
        display: 'flex',
        background: theme.palette.cms?.primary || theme.palette.colorPrimary,
        height: 40,
        padding: '12px 20px',
        fontSize: 14,
        lineHeight: '19px',
        borderRadius: '4px 4px 0 0',
        color: theme.palette.cms?.white || '#ffffff',
        alignItems: 'center',
        '& svg': {
            padding: 2,
        },
        '& span': {
            paddingLeft: 2,
        },
    },
}));

const VariantSubscriptionBox = ({
    brand,
    subscriptionCopy,
    subscriptionDetailsFrequencyValue,
    subscriptionDetailsDurationValue,
    subscriptionConfig,
    isShowingFrequencyDropdown,
    isSubscriptionDurationListEnabled,
    selectValue,
    selectedSku,
    handleChange,
    durationValue,
    subscriptionCheckbox,
    triggerSubscriptionCheck,
}) => {
    const isSmallDevice = useMediaQuery((theme) => theme?.breakpoints.down('sm'));

    const classes = useStyles();

    const hasIcon = subscriptionCopy?.icon?.url || null;

    const renderIconUI = () => hasIcon && (
        <Grid item xs={2} sm={1} className={`${classes.subContainer} ${classes.alignImage}`}>
            <img className={classes.maxWidth} src={subscriptionCopy?.icon?.url} alt="subscription-icon" />
        </Grid>
    );

    const renderSingleLineCopy = () => {
        if (subscriptionCopy?.single_line_copy?.toLowerCase?.().trim?.() === 'n/a') {
            return '';
        }
        if (subscriptionCopy?.single_line_copy) {
            return <ReactMarkdown className={classes.marginNone} source={subscriptionCopy?.single_line_copy} />;
        }
        return `Start a subscription and have it delivered ${subscriptionConfig?.subscription_interval?.[subscriptionDetailsFrequencyValue] || subscriptionDetailsFrequencyValue}`;
    };

    return (
        <Grid container>
            <Grid item xs={12}>
                <div className={classes.subscribeHeader}>
                    <CachedIcon /><span>Subscribe &amp; Save (optional)</span>
                </div>
            </Grid>
            <Grid container className={classes.mainSubscribeBox}>
                <Grid item xs={2} sm={2} md={1} lg={1} xl={1}>
                    <Checkbox
                        id="chk-monthly-subscription"
                        data-test="pdp-check-monthly-subscription"
                        data-testid="pdp-check-monthly-subscription"
                        name="monthlySubscriptionChecked"
                        checked={subscriptionCheckbox}
                        onChange={triggerSubscriptionCheck}
                        classes={{
                            root: classes.checkbox,
                            checked: classes.checked,
                        }}
                    />
                    {isSmallDevice && hasIcon && (
                        <img className={classes.maxWidth} src={subscriptionCopy?.icon?.url} alt="subscription-icon" />
                    )}
                </Grid>
                <Grid xs={10} sm={10} md={11} lg={11} xl={11}>
                    <Grid container>
                        <Grid item xs={12} lg={5} xl={5} md={5} sm={12} className={`${classes.subContainer} ${classes.alignSpan} ${classes.subContainerSm}`}>
                            <Typography variant="body1" className={`${classes.subscriptionCheckBoxContent}`}>
                                {subscriptionCopy?.single_line_label ? <ReactMarkdown className={classes.marginNone} source={subscriptionCopy?.single_line_label} /> : (<><strong>Make this a recurring Shipment</strong><br /></>)}
                                {renderSingleLineCopy()}
                            </Typography>
                        </Grid>
                        {!isSmallDevice && renderIconUI()}
                        <Grid item lg={hasIcon ? 6 : 5} xl={hasIcon ? 6 : 5} md={hasIcon ? 6 : 5} xs={hasIcon ? 10 : 12} sm={hasIcon ? 10 : 12} className={`${classes.subContainer} ${classes.paddingLeft}`}>
                            <div className={classes.subcriptionContent}>
                                {isShowingFrequencyDropdown ? (
                                    <SubscriptionDropDown
                                        selectValue={selectValue}
                                        subscriptionsList={selectedSku.subscriptionsList}
                                        fieldLabel={subscriptionCopy?.frequency_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.frequency_label} /> : 'Frequency:'}
                                        handleChange={handleChange}
                                    />
                                ) : (
                                    <Typography variant="body1">
                                        {subscriptionCopy?.frequency_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.frequency_label} /> : (<strong>Frequency:</strong>)} {subscriptionConfig.subscription_interval[subscriptionDetailsFrequencyValue] || subscriptionDetailsFrequencyValue}
                                    </Typography>
                                )}
                                {isSubscriptionDurationListEnabled ? (
                                    <>
                                        <SubscriptionDropDown
                                            selectValue={durationValue}
                                            subscriptionsList={selectedSku.subscriptionsList}
                                            handleChange={handleChange}
                                            fieldLabel={subscriptionCopy?.duration_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.duration_label} /> : 'Duration:'}
                                            dropdownType="subscription_duration"
                                            frequencyOrDuration="duration"
                                        />
                                    </>
                                ) : (
                                    <Typography variant="body1">
                                        {subscriptionCopy?.duration_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.duration_label} /> : (<strong>Duration:</strong>)}  {subscriptionConfig.subscription_duration[subscriptionDetailsDurationValue] || subscriptionDetailsDurationValue}
                                    </Typography>
                                )}
                            </div>
                            <Typography variant="span" className={`${classes.marginTopBottom10}`} />
                        </Grid>
                    </Grid>
                </Grid>
                {subscriptionCheckbox && (
                    <SubscriptionPDP
                        copy={subscriptionCopy?.offer_copy || ''}
                        brand={brand}
                    />
                )}
            </Grid>
        </Grid>
    );
};

VariantSubscriptionBox.propTypes = {
    subscriptionCopy: shape({
        single_line_label: string,
        frequency_label: string,
        duration_label: string,
    }),
    subscriptionDetailsFrequencyValue: number.isRequired,
    subscriptionDetailsDurationValue: number.isRequired,
    subscriptionConfig: shape({
        subscription_duration: arrayOf(number),
        subscription_interval: arrayOf(number),
    }).isRequired,
    isShowingFrequencyDropdown: bool,
    brand: shape({
        domain: string,
    }).isRequired,

    isSubscriptionDurationListEnabled: bool.isRequired,
    selectValue: string.isRequired,
    selectedSku: shape({
        subscriptionsList: shape({
            type: arrayOf(string),
        }),
    }).isRequired,
    handleChange: func.isRequired,
    durationValue: string.isRequired,
    subscriptionCheckbox: bool.isRequired,
    triggerSubscriptionCheck: func.isRequired,
};

VariantSubscriptionBox.defaultProps = {
    subscriptionCopy: {},
    isShowingFrequencyDropdown: false,
};

export default VariantSubscriptionBox;
